import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/firestore";
import "firebase/compat/auth";
import "firebase/compat/storage";
import "firebase/compat/functions";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

// Consolidate config into a single object for clarity
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Under Jest (NODE_ENV === "test") there are no real credentials, and the
// compat SDK has no initializeTestApp. Initialize a lightweight app so modules
// importing this file load without hitting the network; tests that exercise
// firebase mock this module directly.
const firebaseApp =
  process.env.NODE_ENV === "test"
    ? firebase.initializeApp({
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "demo-test",
        apiKey: "test-api-key",
        appId: "test-app-id",
      })
    : firebase.initializeApp(firebaseConfig);

// Only initialize analytics in production
if (process.env.NODE_ENV === "production") {
  firebase.analytics();
}

// Local / Playwright only: App Check debug token. Vite folds NODE_ENV in
// production builds so this branch is stripped — production init is unchanged.
// Set REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN in .env.sandbox (UUID), then
// register that same UUID in Firebase Console → App Check → this web app →
// Manage debug tokens (project caremap2020). Do not disable enforceAppCheck.
const useFirebaseEmulators =
  typeof window !== "undefined" &&
  process.env.NODE_ENV !== "production" &&
  process.env.NODE_ENV !== "test" &&
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true";

if (
  typeof window !== "undefined" &&
  process.env.NODE_ENV !== "production" &&
  process.env.NODE_ENV !== "test" &&
  !useFirebaseEmulators &&
  process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN
) {
  const debugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
  self.FIREBASE_APPCHECK_DEBUG_TOKEN =
    debugToken === "true" ? true : debugToken;
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(
      process.env.REACT_APP_RECAPTCHA_SITE_KEY || "unused-debug-provider",
    ),
    isTokenAutoRefreshEnabled: true,
  });
}

if (useFirebaseEmulators) {
  const authHost =
    process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST || "127.0.0.1:9099";
  const firestoreHost =
    process.env.REACT_APP_FIRESTORE_EMULATOR_HOST || "127.0.0.1:8080";
  const functionsHost =
    process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || "127.0.0.1:5001";
  const [firestoreName, firestorePort] = firestoreHost.split(":");
  const [functionsName, functionsPort] = functionsHost.split(":");
  firebaseApp.auth().useEmulator(`http://${authHost}`);
  firebaseApp.firestore().useEmulator(firestoreName, Number(firestorePort || 8080));
  firebaseApp.functions().useEmulator(functionsName, Number(functionsPort || 5001));
}

const db = firebaseApp.firestore();
const storage = firebaseApp.storage();

// TEMP dev-only helper: create/associate a "Test Hospital" facility with the
// currently signed-in user, using the client SDK so Firestore rules apply
// (locations create is allowed when the caller's uid is in `users`). Run in the
// browser console on the dev server: `await window.__createTestHospital()`.
// Safe to delete once the test facility exists.
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__createTestHospital = async () => {
    const uid = firebaseApp.auth().currentUser?.uid;
    if (!uid) throw new Error("Sign in first — no authenticated user.");
    await db
      .collection("locations")
      .doc("test-hospital")
      .set(
        {
          id: "test-hospital",
          name: "Test Hospital",
          title: "Test Hospital",
          users: firebase.firestore.FieldValue.arrayUnion(uid),
          isTest: true,
          createdBy: uid,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
    return `Test Hospital ready — associated uid ${uid}`;
  };
}

export default firebaseApp;
export { db, storage };
